<template>
  <div class="w-full">
    <form @submit.prevent="submitForm" class="flex flex-col gap-4">
      <!-- Machine Component Picker -->

      <fieldset class="fieldset">
        <legend class="fieldset-legend">Attach to Component (Optional)</legend>
        <select v-model="formData.machine_component_id" class="select w-full">
          <option :value="null">-- Entire Machine --</option>
          <option v-for="comp in components" :key="comp.id" :value="comp.id">
            {{ comp.name }}
          </option>
        </select>
      </fieldset>

      <!-- Sensor Name -->
      <fieldset class="w-full">
        <legend class="fieldset-legend">Sensor Name *</legend>
        <input
            v-model="formData.name"
            type="text"
            placeholder="e.g., Main Drive Shaft Vibration"
            class="input w-full"
            required
        />
      </fieldset>

      <div class="flex justify-between gap-2">
        <fieldset class="fieldset flex-grow">
          <legend class="fieldset-legend">Serial Number</legend>
          <input v-model="formData.sensor_sn" type="text" placeholder="e.g., 1234567890" class="input w-full"></input>
        </fieldset>

        <fieldset class="fieldset  flex-grow">
          <legend class="fieldset-legend">Hardware uuid</legend>
          <input v-model="formData.hardware_uid" type="text" placeholder="e.g., 1234567890" class="input w-full"></input>
        </fieldset>
      </div>


      <!-- Sensor Type with Auto-Detect -->
      <fieldset class="fieldset">
        <legend class="fieldset-legend">Sensor Type *</legend>
        <select v-model="formData.sensor_type" @change="onTypeSelect" class="select w-full" required>
          <option value="">-- Select Reading Type --</option>
          <option v-for="(p, key) in catalogProfiles" :key="key" :value="key">
            {{ p.display_name }} ({{ p.unit }})
          </option>
          <option value="custom">Custom Type...</option>
        </select>
      </fieldset>

      <div v-if="formData.sensor_type === 'custom'" class="form-group">
        <input
            v-model="formData.custom_sensor_type"
            @input="debounceDetect"
            type="text"
            placeholder="Type custom sensor type..."
            class="input w-full"
        />
      </div>

      <!-- Auto-Detected Suggestion Banner -->
      <div v-if="detectedSuggestion" class="alert-info">
        💡 Auto-configured for <strong>{{ detectedSuggestion.display_name }}</strong>:
        Unit: <code>{{ detectedSuggestion.unit }}</code> |
        Normal Range: <code>{{ detectedSuggestion.default_min }} - {{ detectedSuggestion.default_max }}</code>
      </div>

      <!-- Threshold Controls -->
      <div class="grid grid-cols-3 gap-4">
        <fieldset class="fieldset">
          <legend class="fieldset-legend">Unit *</legend>
          <input v-model="formData.unit" type="text" class="input" required/>

        </fieldset>
        <fieldset class="fieldset">
          <legend class="fieldset-legend">Min Threshold</legend>
          <input v-model.number="formData.min_threshold" type="number" step="any" class="input"/>
        </fieldset>
        <fieldset class="fieldset">
          <legend class="fieldset-legend">Max Threshold</legend>
          <input v-model.number="formData.max_threshold" type="number" step="any" class="input"/>
        </fieldset>

      </div>

      <div class="actions mt-4 my-2 flex justify-end">
        <button class="mr-2" @click="$emit('close')">close</button>
        <button type="submit" class="btn-primary btn btn-sm">{{formData.id ? "Edit Sensor" : "Add Machine Sensor"}}</button>
      </div>
    </form>
  </div>
</template>

<script setup>

import {ref, reactive} from 'vue';
import {upsertSensor} from "~/lib/api/machine_sensors";
import {useRoute} from "vue-router";

const route = useRoute();
const organizationUuid = route.params.organization_uuid;
const machineId = route.params.machine_id;

const props = defineProps({
  machineId: {type: Number, required: true},
  sensor: {type: Object, default: () => ({})},
  components: {type: Array, default: () => []}
});

const emit = defineEmits(['close', 'refresh']);
const submitForm = async () => {
  console.log(">>>>> submitting form", formData);
  try {
    const response = await upsertSensor(organizationUuid, props.machineId || machineId, formData);
    if (response.ok) {
      emit('refresh');
      emit('close');
    }
  } catch (error) {
    console.error('Error submitting form:', error);
  }
}

const catalogProfiles = {
  vibration_acceleration: {display_name: 'Vibration (Acceleration)', unit: 'g', default_min: 0, default_max: 2.5},
  vibration_velocity: {display_name: 'Vibration (Velocity)', unit: 'mm/s', default_min: 0, default_max: 4.5},
  temperature: {display_name: 'Temperature', unit: '°C', default_min: 15, default_max: 85},
  acoustic_ultrasound: {display_name: 'Acoustic Ultrasound', unit: 'dB', default_min: 20, default_max: 75},
  current_mcsa: {display_name: 'Motor Current', unit: 'A', default_min: 0, default_max: 50},
  pressure: {display_name: 'Pressure', unit: 'bar', default_min: 0, default_max: 10}
};
const formData = reactive( props.sensor ?   {...props.sensor} :
    {
      machine_component_id: null,
      name: '',
      sensor_sn: '',
      hardware_uid: '',
      custom_sensor_type: '',
      sensor_type: '',
      unit: '',
      min_threshold: null,
      max_threshold: null,
      status: 'active'
    });
const detectedSuggestion = ref(null);

const onTypeSelect = () => {
  const selected = this.catalogProfiles[this.form.sensor_type];
  if (selected) {
    this.form.unit = selected.unit;
    this.form.min_threshold = selected.default_min;
    this.form.max_threshold = selected.default_max;
    this.detectedSuggestion = selected;
  }
}

const debounceDetect = async () => {
  if (this.customTypeInput.length < 3) return;

  const response = await fetch(`/machines/${this.machineId}/sensors/detect_type?query=${encodeURIComponent(this.customTypeInput)}`);
  const data = await response.json();

  if (data.found) {
    this.form.unit = data.profile.unit;
    this.form.min_threshold = data.profile.default_min;
    this.form.max_threshold = data.profile.default_max;
    this.detectedSuggestion = data.profile;
  }
}


</script>